import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { priceText, mileageText } from '../../lib/financeMath';

/** Collapsible section — progressive disclosure so the screen stays clean. */
export const Section = ({ title, defaultOpen = false, testid, th, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="px-4 mt-4" data-testid={testid}>
            <button
                onClick={() => setOpen((o) => !o)}
                className={`w-full flex items-center justify-between rounded-2xl px-4 py-3.5 text-left transition-colors ${th.section}`}
                data-testid={`${testid}-toggle`}
                aria-expanded={open}
            >
                <span className={`text-sm font-semibold tracking-wide ${th.sectionTitle}`}>{title}</span>
                <ChevronDown className={`w-5 h-5 ${th.muted} transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && <div className="mt-2 animate-in fade-in slide-in-from-top-1 duration-200" data-testid={`${testid}-body`}>{children}</div>}
        </div>
    );
};

/**
 * Specifications / Features & Equipment / Vehicle Details.
 *
 * ONE renderer, used by the dedicated details page AND by the staff preview, so
 * the two can never drift. Every data-testid here is the original one from when
 * these sections lived inline in VehicleShowroom — verify suites depend on them,
 * and only the container and the entry point changed, not the rendering.
 */
export const VehicleSpecsPanel = ({ vehicle: v, th, t, defaultOpen = false, show = 'all' }) => {
    const notes = v?.field_notes || {};
    const features = v?.features || [];

    const rows = [
        [t('usedVehicles.f.year', 'Year'), v?.year, null],
        [t('usedVehicles.f.make', 'Make'), v?.make, null],
        [t('usedVehicles.f.model', 'Model'), v?.model, null],
        [t('compare.f.trim', 'Trim'), v?.trim, null],
        [t('usedVehicles.f.mileage', 'Mileage'), mileageText(v?.mileage), notes.mileage],
        [t('usedVehicles.f.colour', 'Colour'), v?.colour, notes.colour],
        [t('usedVehicles.f.price', 'Asking Price'), priceText(v?.asking_price), notes.price],
        [t('vehicleExp.spec.bodyStyle', 'Body Style'), v?.body_style, null],
        [t('vehicleExp.spec.engine', 'Engine'), v?.engine, null],
        [t('vehicleExp.spec.drivetrain', 'Drivetrain'), v?.drivetrain, null],
        [t('vehicleExp.spec.transmission', 'Transmission'), v?.transmission, null],
        [t('vehicleExp.spec.fuelType', 'Fuel Type'), v?.fuel_type, null],
        [t('vehicleExp.spec.fuelEconomy', 'Fuel Economy'), v?.fuel_economy, null],
        [t('vehicleExp.spec.doors', 'Doors'), v?.doors, null],
        [t('vehicleExp.spec.seating', 'Seating'), v?.seating, null],
        [t('usedVehicles.stockShort', 'Stock'), v?.stock_number, null],
    ].filter(([, val]) => val);

    const showSpecs = show === 'all' || show === 'specs';
    const showDetails = show === 'all' || show === 'details';

    return (
        <>
            {showSpecs && rows.length ? (
                <Section title={t('vehicleExp.specifications', 'Specifications')} defaultOpen={defaultOpen} testid="vs-specs" th={th}>
                    <div className={`rounded-2xl divide-y ${th.panel}`}>
                        {rows.map(([label, val, note]) => (
                            <div key={label} className="flex items-start justify-between gap-4 px-4 py-2.5" data-testid="vs-spec-row">
                                <span className={`text-[11px] uppercase tracking-wide pt-0.5 ${th.label}`}>{label}</span>
                                <span className={`text-sm text-right min-w-0 ${th.body}`}>
                                    {val}
                                    {note ? <span className="block text-xs text-amber-300/80 italic mt-0.5">{note}</span> : null}
                                </span>
                            </div>
                        ))}
                    </div>
                </Section>
            ) : null}

            {showDetails && features.length ? (
                <Section title={t('vehicleExp.featuresEquipment', 'Features & Equipment')} defaultOpen testid="vs-features" th={th}>
                    <div className="grid grid-cols-2 gap-2">
                        {features.map((f, i) => (
                            <div key={i} className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm ${th.panelSolid}`} data-testid="vs-feature">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-amber-400" /></span>
                                <span className="truncate">{f}</span>
                            </div>
                        ))}
                    </div>
                </Section>
            ) : null}

            {showDetails && notes.general ? (
                <Section title={t('vehicleExp.vehicleDetails', 'Vehicle Details')} defaultOpen testid="vs-details" th={th}>
                    <div className={`rounded-2xl p-4 ${th.panelSolid}`}>
                        <p className={`text-[15px] leading-relaxed whitespace-pre-line ${th.body}`} data-testid="vs-description">{notes.general}</p>
                    </div>
                </Section>
            ) : null}
        </>
    );
};

export default VehicleSpecsPanel;
